import { useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Input,
  Label,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { useNetworkList, type ConfigNetwork } from '@/api/opennebula';
import { SectionCard } from './shared';
import { CreateNetworkDialog } from './NetworkDialog';

/** A public network gets an auto-assigned public IP (no static-IP override). */
export function isPublicNet(n: ConfigNetwork): boolean {
  return n.type === 'public' || /public/i.test(n.name ?? '');
}

/** A single attached NIC row, with an optional static private-IP override. */
function NicRow({
  net,
  defaultPrefix,
  onPatch,
  onRemove,
}: {
  net: ConfigNetwork;
  /** The network's default prefix, used to detect/reset an override. */
  defaultPrefix?: string;
  onPatch: (p: Partial<ConfigNetwork>) => void;
  onRemove: () => void;
}) {
  const pub = isPublicNet(net);
  // An override is active when the row's prefix diverges from the default.
  const overridden = (net.prefix ?? '') !== (defaultPrefix ?? '');
  const [override, setOverride] = useState(overridden);

  function toggleOverride(checked: boolean) {
    setOverride(checked);
    // Restore the default prefix when the toggle is turned off.
    if (!checked) onPatch({ prefix: defaultPrefix });
  }

  function resetDefault() {
    setOverride(false);
    onPatch({ prefix: defaultPrefix });
  }

  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-3)',
        border: '1px solid var(--vn-border-default)',
        borderRadius: 'var(--vn-radius-md)',
        padding: 'var(--vn-space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            <span style={{ fontWeight: 500 }}>{net.name}</span>
            <Badge tone={pub ? 'info' : 'neutral'} size="sm">
              {pub ? 'Public' : 'Private'}
            </Badge>
          </div>
          {/* Public ranges are system-managed — legacy hides the CIDR for them. */}
          {!pub && (
            <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              {net.prefix}
            </div>
          )}
        </div>
        <Button type="button" variant="ghost" size="sm" style={{ marginLeft: 'auto' }} onClick={onRemove}>
          <Trash2 size={15} />
        </Button>
      </div>

      {/* Public networks get an auto-assigned public IP — no static-IP override. */}
      {pub ? (
        <div style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          A public IP is auto-assigned from this network for remote access.
        </div>
      ) : (
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', cursor: 'pointer' }}>
          <Checkbox checked={override} onCheckedChange={(c) => toggleOverride(c === true)} />
          <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
            I need to configure the system to use a static IP address.
          </span>
        </label>
      )}

      {!pub && override && (
        <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Label>Static private IP</Label>
            {overridden && (
              <Button type="button" variant="ghost" size="sm" onClick={resetDefault}>
                Reset default
              </Button>
            )}
          </div>
          <Input
            value={net.prefix ?? ''}
            placeholder="Input the IP address you want to assign."
            onChange={(e) => onPatch({ prefix: e.target.value })}
            style={{ maxWidth: 420 }}
          />
        </div>
      )}
    </div>
  );
}

/** Network — attach one or more NICs. */
export function NetworkCard({
  serviceId,
  all,
  selected,
  onChange,
  title = 'Network',
  description = 'Choose a private connection for internal access or a public connection (auto-assigned public IP) for remote access.',
  addPlaceholder = 'Add a network…',
  filter,
}: {
  serviceId?: string;
  all: ConfigNetwork[];
  selected: ConfigNetwork[];
  onChange: (n: ConfigNetwork[]) => void;
  /** Override the section copy — e.g. load balancers attach private backends only. */
  title?: string;
  description?: string;
  addPlaceholder?: string;
  /** Restrict the attachable options — e.g. private-only for load balancers
   *  (whose public frontend NIC is provisioned automatically). */
  filter?: (n: ConfigNetwork) => boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  // Merge the static config networks with the live list so freshly-created
  // networks become attachable without a page reload.
  const { data: live = [] } = useNetworkList(serviceId);
  const options: ConfigNetwork[] = useMemo(() => {
    const merged = new Map<string, ConfigNetwork>();
    all.forEach((n) => merged.set(n.id, n));
    live.forEach((n) => {
      if (!merged.has(n.id)) {
        merged.set(n.id, { id: n.id, name: n.name ?? n.id, prefix: n.prefix ?? n.cidr, type: n.type });
      }
    });
    const list = [...merged.values()];
    return filter ? list.filter(filter) : list;
  }, [all, live, filter]);

  // Default prefix per network id — used to detect/reset a static-IP override.
  const defaultPrefix = useMemo(() => {
    const m = new Map<string, string | undefined>();
    options.forEach((n) => m.set(n.id, n.prefix));
    return m;
  }, [options]);

  const available = options.filter((n) => !selected.some((s) => s.id === n.id));

  function attach(id: string) {
    const n = options.find((x) => x.id === id);
    if (n && !selected.some((s) => s.id === n.id)) onChange([...selected, n]);
  }

  return (
    <SectionCard
      title={title}
      description={description}
      actions={
        serviceId ? (
          <Button type="button" variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={() => setDialogOpen(true)}>
            Create network
          </Button>
        ) : undefined
      }
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
        {selected.map((net, i) => (
          <NicRow
            key={net.id}
            net={net}
            defaultPrefix={defaultPrefix.get(net.id)}
            onPatch={(p) => onChange(selected.map((s, idx) => (idx === i ? { ...s, ...p } : s)))}
            onRemove={() => onChange(selected.filter((_, idx) => idx !== i))}
          />
        ))}
        {available.length > 0 && (
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
            <Select value="" onValueChange={attach}>
              <SelectTrigger style={{ maxWidth: 420 }}>
                <SelectValue placeholder={addPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {available.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                      <Badge tone={isPublicNet(n) ? 'info' : 'neutral'} size="sm">
                        {isPublicNet(n) ? 'Public' : 'Private'}
                      </Badge>
                      {isPublicNet(n) ? n.name : `${n.name} (${n.prefix})`}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {serviceId && (
        <CreateNetworkDialog
          serviceId={serviceId}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onCreated={attach}
        />
      )}
    </SectionCard>
  );
}
